export type HealthFlagStatus = 'up' | 'warn' | 'down';

export type HealthFlagKey =
  | 'newRegistration'
  | 'pipelineStall'
  | 'potentialReg'
  | 'unitMom';

export interface IHealthFlagRange {
  upFrom: number;
  upTo: number;
  warnFrom: number;
  warnTo: number;
  downFrom: number;
  downTo: number;
}

export type IHealthFlagThresholds = Record<HealthFlagKey, IHealthFlagRange>;

export interface IHealthFlagDetail {
  lastNewDonorName?: string;
  lastNewDonorDate?: Date;
  stalledDonors?: { name: string; daysPending: number }[];
  activeDonors?: number;
  totalDonorPool?: number;
  currentUnits?: number;
  prevUnits?: number;
}

export interface IHealthFlagResult {
  key: HealthFlagKey;
  status: HealthFlagStatus;
  label: string;
  value: string;
  subtext: string;
  rawValue: number;
  noData?: boolean;
  detail?: IHealthFlagDetail;
}

export const DEFAULT_ATTN_SCORE_THRESHOLD: IHealthFlagRange = {
  upFrom: 0,
  upTo: 6,
  warnFrom: 7,
  warnTo: 13,
  downFrom: 14,
  downTo: 20,
};

export const DEFAULT_HEALTH_FLAG_THRESHOLDS: IHealthFlagThresholds = {
  newRegistration: {
    upFrom: 0,
    upTo: 30,
    warnFrom: 31,
    warnTo: 60,
    downFrom: 61,
    downTo: 999,
  },
  pipelineStall: {
    upFrom: 0,
    upTo: 30,
    warnFrom: 31,
    warnTo: 60,
    downFrom: 61,
    downTo: 999,
  },
  potentialReg: {
    upFrom: 100,
    upTo: 40,
    warnFrom: 39,
    warnTo: 20,
    downFrom: 19,
    downTo: 0,
  },
  unitMom: {
    upFrom: 3,
    upTo: 1,
    warnFrom: 1,
    warnTo: -1,
    downFrom: -1,
    downTo: -3,
  },
};

/**
 * Determine which status zone a value falls into.
 * lowerBetter: true = UP zone has lower numbers (newReg, pipelineStall)
 * lowerBetter: false = UP zone has higher numbers (potentialReg, unitMom)
 */
export function computeFlagStatus(
  value: number,
  range: IHealthFlagRange,
  lowerBetter: boolean
): HealthFlagStatus {
  if (lowerBetter) {
    // UP: upFrom..upTo, WARN: warnFrom..warnTo, DOWN: downFrom+
    if (value <= range.upTo) return 'up';
    if (value <= range.warnTo) return 'warn';
    return 'down';
  }
  // Higher-better: UP has higher values
  if (value >= range.upTo) return 'up';
  if (value >= range.warnFrom) return 'warn';
  return 'down';
}

export interface ILocationHealthFlagData {
  daysSinceLastRegistration?: number;
  pendingAvgDays?: number;
  activeDonors: number;
  currentUnits: number;
  prevUnits?: number;
  // New detail fields
  lastNewDonorName?: string;
  lastNewDonorDate?: Date;
  stalledDonors?: { name: string; daysPending: number }[];
  totalDonorPool?: number;
}

export function computeLocationHealthFlags(
  data: ILocationHealthFlagData,
  thresholds: IHealthFlagThresholds,
  totalDonorPool: number | null | undefined
): IHealthFlagResult[] {
  const flags: IHealthFlagResult[] = [];

  // 1. New Registration
  const regDays = data.daysSinceLastRegistration ?? 0;
  const regStatus = computeFlagStatus(
    regDays,
    thresholds.newRegistration,
    true
  );
  flags.push({
    key: 'newRegistration',
    status: regStatus,
    label:
      regStatus === 'down'
        ? `NO NEW REG IN ${Math.round(regDays)}+ DAYS`
        : regStatus === 'warn'
        ? `NEW REG ${Math.round(regDays)}D AGO`
        : 'NEW REG RECENT',
    value: `${Math.round(regDays)}d since last reg`,
    subtext:
      regStatus === 'up'
        ? 'Within threshold'
        : regStatus === 'warn'
        ? 'Approaching threshold'
        : 'Below threshold',
    rawValue: regDays,
    detail: {
      lastNewDonorName: data.lastNewDonorName,
      lastNewDonorDate: data.lastNewDonorDate,
    },
  });

  // 2. Pipeline Stall
  const stallDays = data.pendingAvgDays ?? 0;
  const stallStatus = computeFlagStatus(
    stallDays,
    thresholds.pipelineStall,
    true
  );
  flags.push({
    key: 'pipelineStall',
    status: stallStatus,
    label:
      stallStatus === 'down'
        ? `PIPELINE STALL ${Math.round(stallDays)}+ DAYS`
        : stallStatus === 'warn'
        ? `PIPELINE AVG ${Math.round(stallDays)}D`
        : 'PIPELINE HEALTHY',
    value: `${Math.round(stallDays)}d stalled`,
    subtext:
      stallStatus === 'up'
        ? 'Within threshold'
        : stallStatus === 'warn'
        ? 'Approaching threshold'
        : '\u26A0 Donors stuck in pending',
    rawValue: stallDays,
    detail: {
      stalledDonors: data.stalledDonors,
    },
  });

  // 3. Potential Registered
  if (totalDonorPool == null || totalDonorPool <= 0) {
    // No pool data available from CompanyDetails
    flags.push({
      key: 'potentialReg',
      status: 'warn',
      label: 'NO POOL DATA',
      value: 'No Pool Data',
      subtext: 'NoOfLargeDogs not set',
      rawValue: 0,
      noData: true,
      detail: {
        activeDonors: data.activeDonors,
        totalDonorPool: totalDonorPool ?? undefined,
      },
    });
  } else {
    const regPct = Math.round((data.activeDonors / totalDonorPool) * 100);
    const regPctStatus = computeFlagStatus(
      regPct,
      thresholds.potentialReg,
      false
    );
    flags.push({
      key: 'potentialReg',
      status: regPctStatus,
      label:
        regPctStatus === 'down'
          ? `< ${thresholds.potentialReg.warnTo}% POTENTIAL REGISTERED`
          : regPctStatus === 'warn'
          ? `${regPct}% POTENTIAL REGISTERED`
          : 'REGISTRATION ON TARGET',
      value: `${regPct}% registered`,
      subtext:
        (regPctStatus === 'up'
          ? 'Within threshold'
          : regPctStatus === 'warn'
          ? 'Below threshold'
          : '\u26A0 Below threshold') +
        ` (${data.activeDonors} of ${totalDonorPool})`,
      rawValue: regPct,
      detail: {
        activeDonors: data.activeDonors,
        totalDonorPool: totalDonorPool ?? undefined,
      },
    });
  }

  // 4. Unit MoM
  const prevUnits = data.prevUnits ?? 0;
  const momPct =
    prevUnits > 0
      ? Math.round(((data.currentUnits - prevUnits) / prevUnits) * 100)
      : 0;
  const momStatus = computeFlagStatus(momPct, thresholds.unitMom, false);
  flags.push({
    key: 'unitMom',
    status: momStatus,
    label:
      momStatus === 'down'
        ? `> ${Math.abs(thresholds.unitMom.downFrom)}% LESS UNITS MOM`
        : momStatus === 'warn'
        ? `${momPct >= 0 ? '+' : ''}${momPct}% UNITS MOM`
        : 'UNITS TRENDING UP',
    value:
      momStatus === 'warn'
        ? `${Math.abs(momPct)}% MoM`
        : `${momPct >= 0 ? '\u2191' : '\u2193'} ${Math.abs(momPct)}% MoM`,
    subtext:
      momStatus === 'down'
        ? '\u26A0 Significant drop'
        : momStatus === 'warn'
        ? momPct === 0
          ? 'No change'
          : momPct < 0
          ? 'Slight drop'
          : 'Slight rise'
        : 'Significant rise',
    rawValue: momPct,
    detail: {
      currentUnits: data.currentUnits,
      prevUnits: data.prevUnits,
    },
  });

  return flags;
}

export interface IAttnScoreWeightEntry {
  downWeight: number;
  warnWeight: number;
  upWeight: number;
}

export type IAttnScoreWeights = Record<HealthFlagKey, IAttnScoreWeightEntry>;

export const DEFAULT_ATTN_SCORE_WEIGHTS: IAttnScoreWeights = {
  newRegistration: { downWeight: 6, warnWeight: 3, upWeight: 0 },
  pipelineStall: { downWeight: 5, warnWeight: 2, upWeight: 0 },
  potentialReg: { downWeight: 5, warnWeight: 2, upWeight: 0 },
  unitMom: { downWeight: 4, warnWeight: 2, upWeight: 0 },
};

/**
 * Maximum possible attention score = sum of all downWeights (all flags pink).
 */
export function computeMaxAttnScore(
  weights: IAttnScoreWeights = DEFAULT_ATTN_SCORE_WEIGHTS
): number {
  return Object.values(weights).reduce((sum, w) => sum + w.downWeight, 0);
}

/**
 * Compute an attention score for a location.
 * Each flag contributes its downWeight (pink), warnWeight (yellow), or upWeight (green).
 * Weights can be negative to reduce the score for healthy flags.
 * Score is clamped to 0–maxScore.
 */
export function computeLocationAttnScore(
  _locationId: string,
  flags: IHealthFlagResult[],
  weights: IAttnScoreWeights = DEFAULT_ATTN_SCORE_WEIGHTS
): number {
  const raw = flags.reduce((sum, flag) => {
    const w = weights[flag.key];
    if (!w) return sum;
    if (flag.status === 'down') return sum + w.downWeight;
    if (flag.status === 'warn') return sum + w.warnWeight;
    if (flag.status === 'up') return sum + w.upWeight;
    return sum;
  }, 0);
  const max = computeMaxAttnScore(weights);
  return Math.max(0, Math.min(raw, max));
}
